import { useBoolean, useControllableValue } from 'ahooks';
import { Popover } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { TooltipPlacement } from 'antd/lib/tooltip';
import React, { FC, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import ReactSelect from 'react-select';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { isChildNode } from 'utils/is-child-node';
import { Select } from '../creatable';
import { IMultiValue, Option, MultiSelectProps, GroupType } from '../types';
import { SelectContainer } from './common';
import styles from './popover.module.css';
import {
  DropdownIndicator,
  Menu,
  triggerComponents as TriggerComponents,
} from './trigger-components';

export interface TriggerProps {
  size?: SizeType;
  visible: boolean;
  openPopover: () => void;
  closePopover: () => void;
  value: IMultiValue;
  ref: React.RefObject<HTMLElement>;
}

interface MiniSelectProps extends MultiSelectProps {
  triggerComponents?: Partial<typeof TriggerComponents>;
  options?: Option[] | GroupType[];
  onChange?: (value: IMultiValue) => void;
  isClearable?: boolean;
  onClose?: (value: IMultiValue) => void;
  onOpen?: (value: IMultiValue) => void;
  size?: SizeType;
  renderTrigger: (triggerProps: TriggerProps) => JSX.Element;
  placement?: TooltipPlacement;
  overlayClassName?: string;
  readyOnly?: boolean;
}

export const PopoverMultiSelect: FC<MiniSelectProps> = (props) => {
  const {
    options = [],
    onOpen,
    onClose,
    size = 'middle',
    renderTrigger,
    placement = 'bottomLeft',
    overlayClassName,
    readyOnly,
  } = props;
  const [visible, { setTrue: openPopover, setFalse: closePopover }] =
    useBoolean(false);

  const [state, setState] = useControllableValue<IMultiValue>(props, {
    defaultValue: null,
  });
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const selectRef = useRef<ReactSelect>(null);

  useEffect(() => {
    if (visible) {
      if (selectRef.current) {
        selectRef.current.focus();
      }
    }
  }, [visible]);

  const handleChange = (value: IMultiValue) => {
    setState(value);
  };

  const handleVisibleChange = (visibility: boolean) => {
    if (visibility && onOpen) {
      onOpen(state);
    }

    if (!visibility && onClose) {
      onClose(state);
    }
  };

  return (
    <Popover
      placement={placement}
      visible={visible}
      destroyTooltipOnHide
      overlayClassName={`${styles.content} ${overlayClassName}`}
      onVisibleChange={handleVisibleChange}
      content={
        <OutsideClickHandler
          allowed={isChildNode(['popover-multi-select-content'])}
          onOutsideClick={closePopover}
        >
          <SelectContainer ref={ref1} className="popover-multi-select-content">
            <Select
              ref={selectRef}
              type="user"
              menuIsOpen
              autoFocus
              isMulti
              maxMenuHeight={280}
              hideSelectedOptions={false}
              controlShouldRenderValue={false}
              components={{
                DropdownIndicator,
                Menu,
                IndicatorSeparator: () => null,
                ClearIndicator: () => null,
              }}
              placeholder={<FormattedMessage id="placeholder.search" />}
              options={options}
              value={state}
              onChange={handleChange}
            />
          </SelectContainer>
        </OutsideClickHandler>
      }
    >
      {renderTrigger({
        ref: ref2,
        visible,
        closePopover,
        openPopover: readyOnly ? openPopover : () => {},
        value: state,
        size,
      })}
    </Popover>
  );
};
